import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	LinearProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Stack,
	Tab,
	Tabs,
	TableCell,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import api from '@Services/api';
import ManualVcReviewDialog from './ManualVcReviewDialog';
import MainTable from '@Components/MainTable/MainTable';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import { useTypedSelector } from '@Features/store';

export const manualVcAdminBase = '/dashboard/invoices/manual-vc';
const endpoint = 'manual-vc';
type Status = 'draft' | 'pending' | 'approved' | 'rejected';
type Quote = { amount: number; fee: number; total: number };
type Student = {
	id: number;
	first_name: string;
	last_name: string;
	email: string;
	phone_number: string;
	username: string;
};
export type RequestRow = Quote &
	Partial<Student> & {
		reference: string;
		user_id: number;
		status: Status;
		source: string;
		receiver_number: string;
		sending_number: string;
		submitted_at: string;
		reviewed_at: string;
		reviewed_by: number;
		created_by_admin_id: number;
		has_proof: boolean;
		rejection_note: string;
		invoice_id: number | null;
		student?: Student;
		events?: Array<{
			id: number;
			action: string;
			actor_type: string;
			actor_id: number;
			created_at: string;
			note?: string;
		}>;
		followup?: { resolved_at: string; resolution_note: string };
	};
const requestKey = () =>
	Array.from(crypto.getRandomValues(new Uint8Array(16)))
		.map((n) => n.toString(16).padStart(2, '0'))
		.join('');
const date = (value?: string) =>
	value ? new Date(value).toLocaleString() : '—';
const overdue = (r: RequestRow) =>
	r.status === 'pending' &&
	Date.now() - new Date(r.submitted_at).getTime() > 7200000;
const statusChip = (s: Status) => (
	<Chip
		size="small"
		label={s}
		color={
			s === 'approved'
				? 'success'
				: s === 'rejected'
				? 'error'
				: s === 'pending'
				? 'warning'
				: 'default'
		}
	/>
);
function Breakdown({ amount, fee, total }: Quote) {
	return (
		<Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} my={2}>
			<Typography>
				Wallet credit: <strong>{amount} VC</strong>
			</Typography>
			<Typography>
				Fees: <strong>{fee} EGP</strong>
			</Typography>
			<Typography>
				Total transfer: <strong>{total} EGP</strong>
			</Typography>
		</Stack>
	);
}

function Proof({
	reference,
	thumbnail = false,
	onOpen,
}: {
	reference: string;
	thumbnail?: boolean;
	onOpen?: () => void;
}) {
	const [imageLoading, setImageLoading] = useState(true);
	const [url, setUrl] = useState(''),
		[error, setError] = useState(false);
	const host = useRef<HTMLDivElement>(null);
	useEffect(() => {
		let alive = true,
			objectUrl = '',
			started = false;
		setUrl('');
		setImageLoading(true);
		setError(false);
		const load = () => {
			if (started) return;
			started = true;
			api
				.get(`${endpoint}/requests/${reference}/proof`, {
					responseType: 'blob',
				})
				.then((r) => {
					if (alive) {
						objectUrl = URL.createObjectURL(r.data);
						setUrl(objectUrl);
					}
				})
				.catch(() => {
					if (alive) setError(true);
				});
		};
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((e) => e.isIntersecting)) load();
		});
		if (thumbnail && host.current) observer.observe(host.current);
		else load();
		return () => {
			alive = false;
			observer.disconnect();
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [reference, thumbnail]);
	return (
		<Box
			ref={host}
			sx={{
				minHeight: thumbnail ? 50 : 0,
				height: thumbnail ? 'auto' : '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				position: 'relative',
			}}
		>
			{!error && (imageLoading || !url) && (
				<Box
					role="status"
					aria-label="Loading proof image"
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255,255,255,.85)',
						pointerEvents: 'none',
					}}
				>
					<CircularProgress size={24} />
				</Box>
			)}
			{error ? (
				<Typography color="error">Proof unavailable</Typography>
			) : url ? (
				thumbnail ? (
					<button
						aria-label="View payment proof"
						onClick={onOpen}
						style={{ cursor: 'pointer', background: 'none', border: 0 }}
					>
						<img
							src={url}
							onLoad={() => setImageLoading(false)}
							onError={() => setError(true)}
							alt="Transaction proof"
							width={64}
							height={64}
							style={{ objectFit: 'contain' }}
						/>
					</button>
				) : (
					<a
						href={url}
						target="_blank"
						rel="noreferrer"
						aria-label="Open proof at full size"
						style={{
							display: 'block',
							width: '100%',
							height: '100%',
							minHeight: 0,
						}}
					>
						<img
							src={url}
							onLoad={() => setImageLoading(false)}
							onError={() => setError(true)}
							alt="Transaction proof — open full size"
							style={{
								display: 'block',
								width: '100%',
								height: '100%',
								objectFit: 'contain',
							}}
						/>
					</a>
				)
			) : (
				<Typography role="status">Loading proof…</Typography>
			)}
		</Box>
	);
}

export default function ManualVc() {
	const auth = useTypedSelector((s) => s.auth),
		location = useLocation(),
		router = useHistory();
	const navigate = (url: string) => router.push(url);
	const can = (code: string) =>
		auth.user.type === 'super' || Boolean((auth as any).permissions?.has(code));
	const params = new URLSearchParams(location.search),
		reference = params.get('request') || '',
		draftRef = params.get('draft') || '';
	const create = params.get('create') === '1' || Boolean(draftRef);
	const [rows, setRows] = useState<RequestRow[]>([]),
		[detail, setDetail] = useState<RequestRow>(),
		[draft, setDraft] = useState<RequestRow>();
	const [counts, setCounts] = useState({
			pending: 0,
			overdue: 0,
			followups: 0,
		}),
		[page, setPage] = useState(Math.max(1, Number(params.get('page')) || 1)),
		[lastPage, setLastPage] = useState(1);
	const [perPage, setPerPage] = useState(10),
		[total, setTotal] = useState(0);
	const [status, setStatus] = useState('pending'),
		[search, setSearch] = useState(''),
		[from, setFrom] = useState(''),
		[to, setTo] = useState(''),
		[followup, setFollowup] = useState(false);
	const [notice, setNotice] = useState('');
	const [error, setError] = useState(''),
		[busy, setBusy] = useState(false),
		[loading, setLoading] = useState(true),
		[note, setNote] = useState(''),
		[action, setAction] = useState<'approve' | 'reject' | 'resolve' | ''>('');
	const [config, setConfig] = useState<any>(),
		[students, setStudents] = useState<Student[]>([]),
		[student, setStudent] = useState<Student>(),
		[studentQuery, setStudentQuery] = useState('');
	const [amount, setAmount] = useState(100),
		[quote, setQuote] = useState<Quote>(),
		[phone, setPhone] = useState(''),
		[file, setFile] = useState<File>(),
		[preview, setPreview] = useState('');
	const [uploadProgress, setUploadProgress] = useState<number>();
	const [previewLoading, setPreviewLoading] = useState(false);
	const refreshVersion = useRef(0);
	const fail = (e: any) =>
		setError(
			e?.response?.data?.message || 'Something went wrong. Please try again.'
		);
	const listPage = useCallback(
		(targetPage: number) =>
			api.get(`${endpoint}/requests`, {
				params: {
					page: targetPage,
					perPage,
					status: followup ? 'rejected' : status || undefined,
					query: search,
					from: from || undefined,
					to: to || undefined,
					followup: followup ? 'open' : undefined,
				},
			}),
		[perPage, status, search, from, to, followup]
	);
	const refresh = useCallback(async () => {
		const version = ++refreshVersion.current;
		try {
			if (create) {
				const c = await api.get(`${endpoint}/config`);
				if (version !== refreshVersion.current) return;
				setConfig(c.data.data);
				if (draftRef) {
					const result = await api.get(`${endpoint}/drafts/${draftRef}`);
					if (version === refreshVersion.current) setDraft(result.data.data);
				}
			} else {
				const [list, totals] = await Promise.all([
					listPage(page),
					api.get(`${endpoint}/counts`),
				]);
				if (version !== refreshVersion.current) return;
				setRows(list.data.data);
				setLastPage(list.data.meta.last_page);
				setTotal(Number(list.data.meta.total));
				setCounts(totals.data.data);
				if (reference) {
					const result = await api.get(`${endpoint}/requests/${reference}`);
					if (version === refreshVersion.current) setDetail(result.data.data);
				}
			}
		} catch (e) {
			if (version === refreshVersion.current) fail(e);
		} finally {
			if (version === refreshVersion.current) setLoading(false);
		}
	}, [
		create,
		page,
		perPage,
		status,
		search,
		from,
		to,
		followup,
		reference,
		draftRef,
		listPage,
	]);
	useEffect(() => {
		refresh();
		const visibleRefresh = () => {
			if (document.visibilityState === 'visible') refresh();
		};
		const timer = window.setInterval(visibleRefresh, 30000);
		window.addEventListener('focus', visibleRefresh);
		return () => {
			refreshVersion.current++;
			clearInterval(timer);
			window.removeEventListener('focus', visibleRefresh);
		};
	}, [refresh]);
	useEffect(() => {
		setDetail(undefined);
		setAction('');
		setNote('');
	}, [reference]);
	useEffect(() => {
		if (!create || draftRef || !studentQuery.trim()) {
			setStudents([]);
			return;
		}
		let alive = true;
		const timer = setTimeout(
			() =>
				api
					.get(`${endpoint}/students`, { params: { query: studentQuery } })
					.then((r) => {
						if (alive) setStudents(r.data.data);
					})
					.catch(fail),
			300
		);
		return () => {
			alive = false;
			clearTimeout(timer);
		};
	}, [studentQuery, create, draftRef]);
	useEffect(() => {
		if (!create || draftRef) return;
		let alive = true;
		setQuote(undefined);
		const timer = setTimeout(() => {
			if (Number.isInteger(amount) && amount >= 10 && amount <= 5000)
				api
					.get(`${endpoint}/quote`, { params: { amount } })
					.then((r) => {
						if (alive) setQuote(r.data.data);
					})
					.catch(fail);
		}, 200);
		return () => {
			alive = false;
			clearTimeout(timer);
		};
	}, [amount, create, draftRef]);
	useEffect(() => {
		if (!file) {
			setPreview('');
			return;
		}
		const url = URL.createObjectURL(file);
		setPreviewLoading(true);
		setPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [file]);

	const open = (r: RequestRow, targetPage = page) => {
		refreshVersion.current++;
		setDetail(undefined);
		setAction('');
		setNote('');
		setError('');
		setPage(targetPage);
		const next = new URLSearchParams(params);
		next.set('request', r.reference);
		next.set('page', String(targetPage));
		navigate(`${manualVcAdminBase}?${next}`);
	};
	const currentIndex = rows.findIndex((row) => row.reference === reference);
	const canPrevious =
		currentIndex > 0 || page > 1 || (currentIndex < 0 && rows.length > 0);
	const canNext =
		currentIndex < 0
			? rows.length > 0 &&
			  notice !== 'Request approved. No more requests in this view.'
			: currentIndex < rows.length - 1 || page < lastPage;
	const move = async (direction: -1 | 1) => {
		if (busy || !detail) return;
		setBusy(true);
		setNotice('');
		setError('');
		try {
			const targetIndex =
				currentIndex < 0
					? direction < 0
						? rows.length - 1
						: 0
					: currentIndex + direction;
			if (targetIndex >= 0 && targetIndex < rows.length)
				open(rows[targetIndex]);
			else {
				const targetPage = page + direction;
				if (targetPage < 1 || targetPage > lastPage) return;
				const result = await listPage(targetPage);
				const nextRows: RequestRow[] = result.data.data;
				const next =
					direction > 0 ? nextRows[0] : nextRows[nextRows.length - 1];
				if (next) open(next, targetPage);
			}
		} catch (e) {
			fail(e);
		} finally {
			setBusy(false);
		}
	};
	const close = () => {
		const next = new URLSearchParams(params);
		next.delete('request');
		next.delete('create');
		next.delete('draft');
		setDraft(undefined);
		setFile(undefined);
		setNotice('');
		navigate(`${manualVcAdminBase}?${next}`);
	};
	const submit = async () => {
		if (
			!file ||
			!phone.trim() ||
			busy ||
			(!draft && (!student || !quote || quote.amount !== amount))
		)
			return;
		setBusy(true);
		setError('');
		try {
			setUploadProgress(undefined);
			let row = draft;
			if (!row) {
				const payload = `${student!.id}:${amount}`,
					storage = `manual-vc-admin:${auth.user.id}:${payload}`;
				const key = sessionStorage.getItem(storage) || requestKey();
				sessionStorage.setItem(storage, key);
				row = (
					await api.post(
						`${endpoint}/drafts`,
						{ user_id: student!.id, amount },
						{ headers: { 'Idempotency-Key': key } }
					)
				).data.data;
				setDraft(row);
				navigate(`${manualVcAdminBase}?create=1&draft=${row!.reference}`);
				sessionStorage.removeItem(storage);
			}
			const form = new FormData();
			form.append('sending_number', phone);
			form.append('screenshot', file);
			const result = await api.post(
				`${endpoint}/requests/${row!.reference}/submit`,
				form,
				{
					onUploadProgress: (event: { loaded: number; total?: number }) =>
						setUploadProgress(
							event.total
								? Math.min(100, Math.floor((event.loaded * 100) / event.total))
								: undefined
						),
				}
			);
			refreshVersion.current++;
			setDraft(undefined);
			setFile(undefined);
			setPhone('');
			navigate(`${manualVcAdminBase}?request=${result.data.data.reference}`);
		} catch (e) {
			fail(e);
		} finally {
			setBusy(false);
		}
	};
	const tableRequest = useCallback((args: onPageChangeRequestMeta) => {
		setPage(args.activePage || 1);
		setPerPage(args.perPage || 10);
		setSearch(args.query || '');
		const iso = (value?: Date) =>
			value && Number.isFinite(new Date(value).getTime())
				? new Date(value).toISOString()
				: '';
		setFrom(iso(args.startDate));
		setTo(iso(args.endDate));
	}, []);
	const review = async () => {
		if (!detail || !action || busy) return;
		setBusy(true);
		setError('');
		setNotice('');
		const approvedReference = detail.reference;
		try {
			await api.post(`${endpoint}/requests/${approvedReference}/${action}`, {
				note: note.trim(),
			});
			refreshVersion.current++;
			setAction('');
			setNote('');
			if (action !== 'approve') {
				await refresh();
				return;
			}
			// The successful decision is never presented as pending if loading the next request fails.
			setDetail({ ...detail, status: 'approved' });
			setNotice('Request approved.');
			const following = currentIndex >= 0 ? rows[currentIndex + 1] : undefined;
			if (following) {
				open(following);
				return;
			}
			// Pending queues shrink after approval. Re-read this page before moving across its boundary.
			const result = await listPage(page);
			const updated: RequestRow[] = result.data.data;
			const retainedIndex = updated.findIndex(
				(row) => row.reference === approvedReference
			);
			const nextIndex = retainedIndex >= 0 ? retainedIndex + 1 : currentIndex;
			const next = nextIndex >= 0 ? updated[nextIndex] : updated[0];
			if (next) {
				open(next);
				return;
			}
			if (page < Number(result.data.meta.last_page)) {
				const followingPage = await listPage(page + 1);
				if (followingPage.data.data[0]) {
					open(followingPage.data.data[0], page + 1);
					return;
				}
			}
			setNotice('Request approved. No more requests in this view.');
			// Keep the completed request visible and make an emptied last table page usable again.
			if (!updated.length && page > 1)
				open({ ...detail, status: 'approved' }, page - 1);
			else await refresh();
		} catch (e) {
			fail(e);
		} finally {
			setBusy(false);
		}
	};

	if (!can('v-inv') || !can('v-mvc') || (create && !can('c-mvc')))
		return (
			<Alert severity="error">
				You do not have permission to view this page.
			</Alert>
		);
	return (
		<Box p={{ xs: 2, md: 3 }}>
			<Typography variant="h4" mb={2}>
				Manual VC Requests
			</Typography>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
					{error}
				</Alert>
			)}
			{loading && <Typography role="status">Loading…</Typography>}
			{create ? (
				<Dialog open onClose={busy ? undefined : close} maxWidth="sm" fullWidth>
					<DialogTitle>Add VC Request</DialogTitle>
					<DialogContent>
						{error && <Alert severity="error">{error}</Alert>}
						{!config?.enabled && !draft && (
							<Alert severity="warning">
								New manual payments are disabled.
							</Alert>
						)}
						{!draft ? (
							<>
								<TextField
									fullWidth
									label="Search student by name, email, ID, username or phone"
									value={studentQuery}
									onChange={(e) => {
										setStudentQuery(e.target.value);
										setStudent(undefined);
									}}
									sx={{ my: 2 }}
								/>
								<Stack spacing={1}>
									{students.map((s) => (
										<Button
											key={s.id}
											variant={student?.id === s.id ? 'contained' : 'outlined'}
											onClick={() => setStudent(s)}
										>
											{s.first_name} {s.last_name} · #{s.id} ·{' '}
											{s.email || s.phone_number}
										</Button>
									))}
								</Stack>
								{student && (
									<Alert severity="info" sx={{ my: 2 }}>
										Selected: {student.first_name} {student.last_name} (#
										{student.id})
									</Alert>
								)}
								<TextField
									fullWidth
									type="number"
									label="VC amount"
									inputProps={{ min: 10, max: 5000, step: 1 }}
									value={amount}
									onChange={(e) => setAmount(Number(e.target.value))}
									sx={{ my: 2 }}
								/>
								{quote && <Breakdown {...quote} />}
								<Typography>
									Receiver: {config?.receiver_number || 'Not configured'}
								</Typography>
							</>
						) : (
							<>
								<Typography>
									Student #{draft.user_id} · {draft.reference}
								</Typography>
								<Breakdown {...draft} />
								<Typography>Receiver: {draft.receiver_number}</Typography>
							</>
						)}
						<TextField
							fullWidth
							required
							disabled={busy}
							label="Sending phone number"
							type="tel"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							sx={{ my: 2 }}
						/>
						<Button component="label" variant="outlined">
							Upload screenshot
							<input
								hidden
								disabled={busy}
								type="file"
								accept="image/jpeg,image/png,image/webp"
								onChange={(e) => {
									const next = e.target.files?.[0];
									if (
										next &&
										(next.size > 10 * 1024 * 1024 ||
											!['image/jpeg', 'image/png', 'image/webp'].includes(
												next.type
											))
									) {
										setError('Choose a JPEG, PNG or WebP image up to 10 MB.');
										setFile(undefined);
										e.target.value = '';
									} else {
										setFile(next);
										setError('');
									}
								}}
							/>
						</Button>
						<Typography variant="body2" mt={1}>
							JPEG, PNG or WebP · maximum 10 MB
						</Typography>
						{preview && previewLoading && (
							<Stack
								role="status"
								direction="row"
								gap={1}
								alignItems="center"
								mt={2}
							>
								<CircularProgress size={20} />
								<Typography variant="body2">Loading image…</Typography>
							</Stack>
						)}
						{create && busy && (
							<Box role="status" aria-live="polite" sx={{ my: 2 }}>
								<Typography variant="body2">
									{uploadProgress === 100
										? 'Upload complete. Saving request…'
										: `Uploading screenshot…${
												uploadProgress !== undefined
													? ` ${uploadProgress}%`
													: ''
										  }`}
								</Typography>
								<LinearProgress
									sx={{ mt: 1 }}
									variant={
										uploadProgress === undefined || uploadProgress === 100
											? 'indeterminate'
											: 'determinate'
									}
									value={uploadProgress}
								/>
							</Box>
						)}
						{preview && (
							<Box
								component="img"
								src={preview}
								key={preview}
								onLoad={() => setPreviewLoading(false)}
								onError={() => {
									setPreviewLoading(false);
									setFile(undefined);
									setError(
										'Unable to load image. Choose a valid JPEG, PNG or WebP.'
									);
								}}
								alt="Proof preview"
								sx={{
									display: 'block',
									maxWidth: '100%',
									maxHeight: 300,
									my: 2,
								}}
							/>
						)}
					</DialogContent>
					<DialogActions>
						<RoundButton disabled={busy} onClick={close}>
							Cancel
						</RoundButton>
						<RoundButton
							variant="contained"
							disabled={
								!can('c-mvc') ||
								busy ||
								!file ||
								!phone.trim() ||
								(!draft &&
									(!student ||
										!quote ||
										quote.amount !== amount ||
										!config?.enabled))
							}
							onClick={submit}
						>
							{busy ? 'Submitting…' : 'Create Pending Request'}
						</RoundButton>
					</DialogActions>
				</Dialog>
			) : (
				<>
					<Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
						<Chip label={`${counts.pending} pending`} />
						<Chip
							label={`${counts.overdue} waiting over 2 hours`}
							color={counts.overdue ? 'warning' : 'default'}
						/>
						{can('c-mvc') && (
							<RoundButton
								variant="contained"
								onClick={() => {
									setDraft(undefined);
									setFile(undefined);
									navigate(`${manualVcAdminBase}?create=1`);
								}}
							>
								Add Request
							</RoundButton>
						)}
					</Stack>
					<Tabs
						value={followup ? 1 : 0}
						onChange={(_, value) => {
							setFollowup(value === 1);
							setPage(1);
						}}
					>
						<Tab label="Requests" />
						<Tab label={`Operations Follow-up (${counts.followups})`} />
					</Tabs>
					<Box my={2}>
						{' '}
						{!followup && (
							<TextField
								select
								label="Status"
								value={status}
								onChange={(e) => {
									setStatus(e.target.value);
									setPage(1);
								}}
								sx={{ minWidth: 150 }}
							>
								{['pending', 'approved', 'rejected', ''].map((s) => (
									<MenuItem key={s} value={s}>
										{s || 'All statuses'}
									</MenuItem>
								))}
							</TextField>
						)}
					</Box>
					<MainTable
						tableHeads={[
							'Student',
							'Sending number',
							'VC / Fee / Total',
							'Proof',
							'Submitted / Waiting',
							'Source',
							'Status',
							'Reviewed by',
							'Reviewed at',
							'',
						].map((displayName) => ({
							displayName,
							fieldName: displayName,
							canSort: false,
						}))}
						tableFilters={[]}
						totalPages={lastPage}
						total={total}
						pageNum={page}
						RowsperPage={perPage}
						onPageChangeRequest={tableRequest}
						renderItems={() => (
							<>
								{rows.map((r) => (
									<TableRow
										key={r.reference}
										sx={overdue(r) ? { backgroundColor: '#fff6df' } : {}}
									>
										<TableCell>
											<strong>
												{r.first_name} {r.last_name}
											</strong>
											<br />
											{r.username || 'Username unavailable'}
											<br />
											{r.email}
											<br />
											{r.phone_number}
										</TableCell>
										<TableCell>{r.sending_number}</TableCell>
										<TableCell>
											{r.amount} VC / {r.fee} EGP /{' '}
											<strong>{r.total} EGP</strong>
										</TableCell>
										<TableCell>
											<Proof
												reference={r.reference}
												thumbnail
												onOpen={() => open(r)}
											/>
										</TableCell>
										<TableCell>
											{date(r.submitted_at)}
											{r.status === 'pending' && (
												<Typography variant="body2">
													{Math.max(
														0,
														Math.floor(
															(Date.now() -
																new Date(r.submitted_at).getTime()) /
																60000
														)
													)}{' '}
													minutes waiting
												</Typography>
											)}
										</TableCell>
										<TableCell>{r.source}</TableCell>
										<TableCell>{statusChip(r.status)}</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											{r.reviewed_by != null ? `Admin #${r.reviewed_by}` : '—'}
										</TableCell>
										<TableCell>{date(r.reviewed_at)}</TableCell>
										<TableCell>
											<Button onClick={() => open(r)}>View</Button>
										</TableCell>
									</TableRow>
								))}
								{!rows.length && !loading && (
									<TableRow>
										<TableCell colSpan={10}>
											No requests match these filters.
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					/>
					<ManualVcReviewDialog
						open={Boolean(reference)}
						request={detail}
						busy={busy}
						error={error}
						notice={notice}
						canReview={can('r-mvc')}
						canResolve={can('f-mvc')}
						canViewInvoice={can('v-inv')}
						onClose={close}
						onPrevious={() => move(-1)}
						onNext={() => move(1)}
						canPrevious={canPrevious}
						canNext={canNext}
						action={action}
						note={note}
						onNote={setNote}
						onAction={(value) => {
							setAction(value);
							setNote('');
						}}
						onConfirm={review}
						proof={
							detail?.has_proof ? (
								<Proof key={detail.reference} reference={detail.reference} />
							) : undefined
						}
					/>
				</>
			)}
		</Box>
	);
}
